import * as React from 'react';
import { Search, Columns3, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Button } from './button';
import { Input } from './input';
import { TablePager } from './pagination';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from './dropdown-menu';

/** Toolbar + footer chrome around the `Table` primitives: they own the data, these own the shell. */

// Toolbar row — a flex container. Place <TableSearch>, filter tabs, a
// <ColumnsToggle> and a <TableCount> (which floats right) inside it.
export const DataTableToolbar = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div className={cn('flex flex-wrap items-center gap-3', className)} {...props} />
);
DataTableToolbar.displayName = 'DataTableToolbar';

interface TableSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
}
// Search field with a leading icon; 280px on the filter line.
export const TableSearch = ({ value, onChange, placeholder, className, ...rest }: TableSearchProps) => (
  <div className={cn('relative w-full sm:w-72', className)}>
    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-9"
      {...rest}
    />
  </div>
);
TableSearch.displayName = 'TableSearch';

export interface SegmentedTabOption<T extends string> {
  label: string;
  value: T;
  /** Shown after the label in a lighter weight, e.g. how many items the view holds. */
  count?: number;
}
interface SegmentedTabsProps<T extends string> {
  options: SegmentedTabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  'aria-label'?: string;
}
// Views of the same data (period, role, band …): a gray track with the current
// option raised in white. A list's main status uses StatusTabs instead.
export function SegmentedTabs<T extends string>({
  options,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: SegmentedTabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'no-scrollbar inline-flex h-9 max-w-full items-center gap-0.5 overflow-x-auto rounded-md bg-muted p-[3px]',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex h-full shrink-0 items-center whitespace-nowrap rounded-sm px-3 text-[13px] transition-colors',
              active
                ? 'bg-card font-semibold text-foreground shadow-sm'
                : 'font-medium text-muted-foreground hover:text-foreground'
            )}
          >
            {opt.label}
            {opt.count != null && (
              <span className="ml-1.5 font-normal tabular-nums text-muted-foreground">{opt.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
SegmentedTabs.displayName = 'SegmentedTabs';

export interface ColumnToggle {
  id: string;
  label: string;
  visible: boolean;
  /** Columns the user shouldn't be able to hide (e.g. the primary column). */
  locked?: boolean;
}
interface ColumnsToggleProps {
  columns: ColumnToggle[];
  onChange: (id: string, visible: boolean) => void;
  className?: string;
}
// "Columns" dropdown with a show/hide checkbox per column.
export const ColumnsToggle = ({ columns, onChange, className }: ColumnsToggleProps) => {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={cn('gap-1.5', className)}>
          <Columns3 className="h-4 w-4" aria-hidden />
          {t('columns')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {columns.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.id}
            checked={col.visible}
            disabled={col.locked}
            onCheckedChange={(checked) => onChange(col.id, checked === true)}
            onSelect={(e) => e.preventDefault()}
          >
            {col.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
ColumnsToggle.displayName = 'ColumnsToggle';

// Right-aligned "N items" count for the toolbar.
export const TableCount = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span className={cn('ml-auto whitespace-nowrap text-sm text-muted-foreground', className)} {...props} />
);
TableCount.displayName = 'TableCount';

// Monospace chip for id / reference / code cells. Never wraps — a code split
// across lines at its hyphens is unreadable, so the table scrolls instead.
export const RefChip = ({ className, ...props }: React.ComponentProps<'code'>) => (
  <code
    className={cn(
      'inline-flex items-center whitespace-nowrap rounded-md bg-muted px-2 py-1 font-mono text-xs text-muted-foreground',
      className
    )}
    {...props}
  />
);
RefChip.displayName = 'RefChip';

interface CopyRefChipProps {
  /** The reference/code to render and copy. */
  value: string;
  /** Accessible name for the copy action, e.g. "Copy code". */
  label: string;
  /** Announced/shown once the value is on the clipboard. */
  copiedLabel?: string;
  className?: string;
}
// A RefChip whose whole surface copies its value on click. The trailing icon
// stays visible (not hover-only) so the affordance survives on touch screens.
export const CopyRefChip = ({ value, label, copiedLabel, className }: CopyRefChipProps) => {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    // No clipboard in insecure contexts — leave the chip unchanged rather than
    // flashing a success state for a copy that never happened.
    navigator.clipboard?.writeText(value).then(
      () => setCopied(true),
      () => undefined
    );
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? copiedLabel ?? label : label}
      aria-label={label}
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-md bg-muted px-2 py-1 font-mono text-xs text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
    >
      <span className="truncate">{value}</span>
      {copied ? (
        <Check className="h-3 w-3 shrink-0 text-success-600" aria-hidden />
      ) : (
        <Copy className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
      )}
      <span className="sr-only" aria-live="polite">
        {copied ? copiedLabel ?? label : ''}
      </span>
    </button>
  );
};
CopyRefChip.displayName = 'CopyRefChip';

interface DataTablePaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages (>= 1). */
  pageCount: number;
  pageSize: number;
  /** Total number of entries across all pages. */
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  /** Already-translated noun after the total, e.g. "ta test". */
  noun?: string;
  className?: string;
}
// Table footer pager — the shared `TablePager` with the page-size control on.
export const DataTablePagination = ({ className, ...props }: DataTablePaginationProps) => (
  <TablePager className={cn('pb-6', className)} {...props} />
);
DataTablePagination.displayName = 'DataTablePagination';
